/**
 * CustomerPlanAssignmentPanel — Account Detail panel for assigning a
 * customer plan to the user's customer profile.
 *
 * Renders ONLY when the user has the Customer capability (is_customer).
 * The dropdown lists every active customer_plan row sorted by sort_order.
 * Free is the default and the only way to "clear perks" — null is NOT
 * allowed (mirrors server-side guard).
 *
 * NOTE: customer_plan_id is currently DISPLAY-ONLY. Booking math does
 * NOT consume the plan's perk fields (member_discount_pct,
 * waive_securement_fee, priority_scheduling). The subtitle copy
 * surfaces this so admins don't get confused.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getPlanDisplayName } from "@/lib/planLabels";

export default function CustomerPlanAssignmentPanel({ detail, onSaved }) {
  const isCustomer = !!detail?.is_customer;
  const currentPlan = detail?.customer_plan; // joined plan doc
  const currentPlanId =
    currentPlan?.id ||
    detail?.customer_profile?.customer_plan_id ||
    null;

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(currentPlanId || "");
  const [saving, setSaving] = useState(false);

  // Pull the plan registry once on mount. Small list — no pagination.
  useEffect(() => {
    if (!isCustomer) {
      setPlansLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/customer-plans");
        if (!cancelled) setPlans(data || []);
      } catch (e) {
        if (!cancelled) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCustomer]);

  // Re-sync when parent re-fetches detail.
  useEffect(() => {
    setSelectedId(currentPlanId || "");
  }, [currentPlanId]);

  const dirty = useMemo(
    () => !!selectedId && selectedId !== currentPlanId,
    [selectedId, currentPlanId]
  );

  if (!isCustomer) return null;

  const submit = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/admin/accounts/${detail.id}/customer-plan`,
        { customer_plan_id: selectedId }
      );
      toast.success("Customer plan updated.");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-5" data-testid="customer-plan-panel">
      <hr className="mb-5 border-pp-border" />
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-brand-orange" aria-hidden="true" />
        <h4 className="font-heading text-lg font-semibold text-pp-text">
          Customer plan
        </h4>
      </div>
      <p
        className="mb-4 text-xs text-pp-text-muted"
        data-testid="customer-plan-subtitle"
      >
        Display only — plan perks are not yet wired into booking math.
        Assign &ldquo;Basic&rdquo; to clear perks.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="customer-plan-current-row">
        <span className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
          Current
        </span>
        {currentPlan ? (
          <span className="font-semibold text-pp-text" data-testid="customer-plan-current-name">
            {getPlanDisplayName(currentPlan.id)}
          </span>
        ) : (
          <span className="text-pp-text-muted" data-testid="customer-plan-current-none">
            — unassigned —
          </span>
        )}
      </div>

      <div className="grid items-end gap-3 sm:grid-cols-[1fr_auto]">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
            Change plan
          </Label>
          <Select
            value={selectedId}
            onValueChange={setSelectedId}
            disabled={plansLoading || saving}
          >
            <SelectTrigger
              className="mt-2 h-11 rounded-full"
              data-testid="customer-plan-select-trigger"
            >
              <SelectValue placeholder={plansLoading ? "Loading plans…" : "Pick a plan"} />
            </SelectTrigger>
            <SelectContent>
              {plans.map((p) => (
                <SelectItem
                  key={p.id}
                  value={p.id}
                  data-testid={`customer-plan-select-option-${p.id}`}
                >
                  {getPlanDisplayName(p.id)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          onClick={submit}
          disabled={!dirty || saving || plansLoading}
          className="h-11 rounded-full bg-pp-orange px-6 text-pp-cta-text hover:bg-pp-orange-hover"
          data-testid="customer-plan-save-button"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
            </>
          ) : (
            "Save plan"
          )}
        </Button>
      </div>
    </section>
  );
}
